import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  Building2,
  Calendar,
  Filter,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = [
  { value: "new", label: "Novo" },
  { value: "in_progress", label: "Em andamento" },
  { value: "won", label: "Ganho" },
  { value: "lost", label: "Perdido" },
];

function statusBadge(s) {
  const map = {
    new: "bg-brand/10 text-brand border-brand/20",
    in_progress: "bg-bronze/10 text-bronze border-bronze/30",
    won: "bg-emerald-50 text-emerald-700 border-emerald-200",
    lost: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return map[s] || map.new;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");

  const fetchLeads = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const filtered = useMemo(() => {
    if (!leads) return [];
    if (filter === "all") return leads;
    return leads.filter((l) => l.status === filter);
  }, [leads, filter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((cur) => cur.map((l) => (l.id === id ? data : l)));
      toast.success("Status atualizado.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((cur) => cur.filter((l) => l.id !== id));
      toast.success("Lead removido.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const counts = useMemo(() => {
    const base = { all: 0, new: 0, in_progress: 0, won: 0, lost: 0 };
    if (!leads) return base;
    base.all = leads.length;
    for (const l of leads) base[l.status] = (base[l.status] || 0) + 1;
    return base;
  }, [leads]);

  return (
    <div className="min-h-screen bg-surface-alt">
      <header className="bg-white border-b border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-brand text-white font-display font-black text-sm">
              W
            </span>
            <div>
              <div className="font-display font-bold text-ink text-sm">
                Painel Wellpec
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-bronze">
                Gestão de leads
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:block text-ink-muted">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout"
              onClick={doLogout}
              className="inline-flex items-center gap-2 border border-surface-line rounded-sm px-4 py-2 text-ink hover:bg-surface-alt"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">
              Leads recebidos
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              Todos os formulários de orçamento e contato do site.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-ink-muted" />
            <select
              data-testid="admin-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-sm border border-surface-line bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mid"
            >
              <option value="all">Todos ({counts.all})</option>
              {STATUS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label} ({counts[s.value] || 0})
                </option>
              ))}
            </select>
          </div>
        </div>

        {err && (
          <div
            data-testid="admin-error"
            className="mt-6 p-4 rounded-sm border border-destructive/30 bg-destructive/5 text-destructive text-sm"
          >
            {err}
          </div>
        )}

        <div className="mt-8">
          {leads === null ? (
            <div className="flex items-center gap-2 text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando leads...
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="admin-empty"
              className="p-10 bg-white border border-surface-line rounded-sm text-center text-ink-muted"
            >
              <CheckCircle2 className="h-8 w-8 mx-auto text-brand" />
              <p className="mt-3">Nenhum lead no filtro atual.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {filtered.map((l) => (
                <li
                  key={l.id}
                  data-testid={`admin-lead-${l.id}`}
                  className="p-6 bg-white border border-surface-line rounded-sm shadow-sm"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-display font-bold text-ink text-lg">
                          {l.name}
                        </h3>
                        <span
                          className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold ${statusBadge(
                            l.status
                          )}`}
                        >
                          {STATUS.find((s) => s.value === l.status)?.label ||
                            l.status}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                          <Calendar className="h-3 w-3" />
                          {new Date(l.created_at).toLocaleString("pt-BR")}
                        </span>
                        <span className="text-xs text-bronze font-semibold uppercase tracking-wider">
                          · {l.source}
                        </span>
                      </div>
                      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm text-ink">
                        <a
                          href={`mailto:${l.email}`}
                          className="inline-flex items-center gap-2 hover:text-brand break-all"
                        >
                          <Mail className="h-4 w-4 text-bronze shrink-0" />
                          {l.email}
                        </a>
                        <span className="inline-flex items-center gap-2">
                          <Phone className="h-4 w-4 text-bronze shrink-0" />
                          {l.phone}
                        </span>
                        {l.company && (
                          <span className="inline-flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-bronze shrink-0" />
                            {l.company}
                          </span>
                        )}
                      </div>
                      {l.subject && (
                        <div className="mt-3 text-sm">
                          <span className="font-semibold text-ink">Assunto: </span>
                          <span className="text-ink-muted">{l.subject}</span>
                        </div>
                      )}
                      <p className="mt-3 text-sm text-ink-muted whitespace-pre-line">
                        {l.message}
                      </p>
                    </div>

                    <div className="flex md:flex-col gap-2 md:items-end">
                      <select
                        data-testid={`lead-status-${l.id}`}
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className="rounded-sm border border-surface-line bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mid"
                      >
                        {STATUS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <button
                        data-testid={`lead-delete-${l.id}`}
                        onClick={() => remove(l.id)}
                        className="inline-flex items-center justify-center gap-2 border border-surface-line rounded-sm px-3 py-2 text-sm text-ink hover:bg-surface-alt hover:border-destructive/30 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" /> Remover
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
